import { useCallback } from "react";
import { Divider, Stack, Title, Text, Group, ThemeIcon } from "@mantine/core";
import { IconPackage } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useBatchSubmit, useSharedBatchJobs } from "@hooks/batch";
import { batchApi } from "@services/batchApi";
import type { BatchJobEntry, TextUnit } from "@models/batch";
import { BatchUploadPanel } from "./BatchUploadPanel";
import { BatchJobsTable } from "./BatchJobsTable";

/**
 * Main Batch Translation view — combines the upload panel + jobs table.
 *
 * Batch-job state and the automatic 8s poll loop live in BatchJobsProvider
 * (above the Tabs) so the poll keeps running regardless of the active tab and
 * the tab badge shares the same state. This view consumes that shared state
 * and only handles submit / manual refresh / download.
 */
export const BatchTranslationView: React.FC = () => {
  const { t } = useTranslation();
  const { jobs, add, update, remove, clear } = useSharedBatchJobs();
  const { submitting, error, submit } = useBatchSubmit();

  const handleSubmit = useCallback(
    async (opts: {
      sourceLang: string;
      targetLang: string;
      model: string;
      textUnits: TextUnit[];
      fileName: string;
    }) => {
      const entry = await submit(opts);
      if (entry) {
        add(entry);
      }
    },
    [submit, add],
  );

  const handleRefresh = useCallback(
    async (jobId: string) => {
      try {
        const res = await batchApi.getJobStatus(jobId);

        let status: BatchJobEntry["status"] = "pending";
        if (res.global_status === "completed") status = "completed";
        else if (res.global_status === "failed") status = "failed";
        else if (res.global_status === "queued") status = "queued";

        let completedUnits = 0;
        let totalUnits = 0;
        if (res.validation) {
          const reports = Object.values(res.validation);
          completedUnits = reports.reduce(
            (sum, v) => sum + (v.received_count ?? 0),
            0,
          );
          totalUnits = reports.reduce(
            (sum, v) => sum + (v.expected_count ?? 0),
            0,
          );
        }

        const resultsUrl = res.presigned_urls
          ? Object.values(res.presigned_urls).find((url) => url.startsWith("http"))
          : undefined;

        update(jobId, {
          status,
          completedUnits,
          ...(totalUnits > 0 ? { totalUnits } : {}),
          resultsUrl,
          error: res.error ?? undefined,
        });
      } catch {
        // ignore
      }
    },
    [update],
  );

  const handleDownload = useCallback(async (job: BatchJobEntry) => {
    if (!job.resultsUrl || !job.resultsUrl.startsWith("http")) return;
    // Open in new tab — presigned S3 URL
    window.open(job.resultsUrl, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <Stack gap="lg">
      <BatchUploadPanel
        onSubmit={handleSubmit}
        submitting={submitting}
        error={error}
      />

      <Divider />

      <Group gap="xs">
        <ThemeIcon size={24} variant="light" color="brand" radius="sm">
          <IconPackage size={14} />
        </ThemeIcon>
        <Title order={5} ff="heading">
          {t("batch.jobsTitle")}
        </Title>
        <Text size="xs" c="dimmed">
          — {t("batch.jobsSubtitle")}
        </Text>
      </Group>

      <BatchJobsTable
        jobs={jobs}
        onRefresh={handleRefresh}
        onDownload={handleDownload}
        onRemove={remove}
        onClear={clear}
      />
    </Stack>
  );
};
